<template>
  <data-table
    :headers="headers"
    :items="sortedRows"
    :options.sync="options"
    :no-data-text="noDataText"
    :loading="loading"
    externallySorted
    checkbox
    v-model="selectedRows"
  >
    <template #item.locomotion="{ item }">
      <template v-if="featureIsActive('s_type_crawling_pests')">
        <span class="trap-tag d-inline-flex align-center">
          <insect-locomotion-icon :locomotion="item.locomotion" :size="14" />
          {{ term(`station.locomotion_label.${item.locomotion}`) }}
        </span>
      </template>
    </template>
    <template #item.installed_at="{ item }">
      {{ formatDate(item.installed_at) }}
    </template>
    <template #item.collected_at="{ item }">
      {{ formatDate(item.collected_at) }}
    </template>
    <template #item.upload_time="{ item }">
      {{ formatDateTime(item.upload_time) }}
    </template>
    <template #item.photoCount="{ item }">
      {{ hideWhileProcessing(item, item.photoCount) }}
    </template>
    <template #item.insectCount="{ item }">
      {{ hideWhileProcessing(item, item.insectCount) }}
    </template>
    <template #item.actions="{ item }">
      <span v-if="item.processing">
        {{ $t('processing') }}
      </span>
      <template v-else>
        <v-tooltip bottom v-if="isTimedOut(item)">
          <template #activator="{ on, attrs }">
            <v-btn
              text
              icon
              @click.stop="viewEvent(item)"
              data-testid="view_button_timeout"
              v-on="on"
              v-bind="attrs"
            >
              <v-badge color="warning" overlap bottom icon="mdi-alert">
                <v-icon primary class="ma-2 material-icons">collections</v-icon>
              </v-badge>
            </v-btn>
          </template>
          {{ $t('warning.list_row') }}
        </v-tooltip>
        <v-btn v-else text icon @click.stop="viewEvent(item)" data-testid="view_button">
          <v-icon primary class="ma-2 material-icons">collections</v-icon>
        </v-btn>
        <v-btn text icon @click.stop="editEvent(item)" data-testid="edit_button">
          <v-icon primary class="ma-2">fa-pencil</v-icon>
        </v-btn>
      </template>
      <station-event-polling
        :event="events.find(e => e.id === item.id)"
        @eventUpdated="event => $emit('eventUpdated', event)"
      />
    </template>
  </data-table>
</template>

<i18n lang="yaml">
ja:
  loading: '読込中'
  no_data: 'データがありません'
  processing: '解析中...'
  warning:
    list_row: '編集内容の反映に時間がかかっています。詳細を開いて操作を選択してください'

en:
  loading: 'Now loading'
  no_data: 'No data available'
  processing: 'Processing...'
  warning:
    list_row: 'The edit is taking longer than expected. Open the detail view to choose an action.'
</i18n>

<script>
import LocalizedTerms from '@/mixins/localizedTerms';
import DateTimeFormatting from '@/mixins/dateTimeFormatting';
import Features from '@/mixins/features';
import DataTable from '@/components/atoms/DataTable';
import StationEventPolling from '@/components/StationEventPolling';
import InsectLocomotionIcon from '@/components/atoms/InsectLocomotionIcon';
import { locomotionOf } from '@/libs/insectTypes';
import { isEditingTimedOut } from '@/libs/isEditingTimedOut';

export default {
  name: 'station-place-event-table',
  components: {
    DataTable,
    StationEventPolling,
    InsectLocomotionIcon,
  },
  mixins: [Features, LocalizedTerms, DateTimeFormatting],
  props: {
    events: Array,
    loading: Boolean,
  },
  data() {
    return {
      options: {
        sortBy: ['upload_time'],
        sortDesc: [true],
        itemsPerPage: 100,
      },
      selectedRows: [],
    };
  },
  computed: {
    headers() {
      const locomotionHeader = this.featureIsActive('s_type_crawling_pests')
        ? [{ text: this.term('station.locomotion'), value: 'locomotion', sortable: true, align: 'center', width: 140 }]
        : [];
      return [
        ...locomotionHeader,
        { text: this.term('station.trap_no'), value: 'trap_no' },
        { text: this.term('station.month'), value: 'month', width: 100 },
        { text: this.term('station.installed_at'), value: 'installed_at', width: 140 },
        { text: this.term('station.collected_at'), value: 'collected_at', width: 140 },
        { text: this.term('station.upload_time'), value: 'upload_time', width: 200 },
        { text: this.term('station.photo_count'), value: 'photoCount', width: 130 },
        { text: this.term('station.insect_count'), value: 'insectCount', width: 130 },
        { text: '', value: 'actions', sortable: false, width: 120 },
      ];
    },
    eventRows() {
      return this.events.map(this.buildEventRow);
    },
    noDataText() {
      if (this.loading) {
        return this.$t('loading');
      }

      return this.$t('no_data');
    },
    sortedSelectedRows() {
      const selectedIds = new Set(this.selectedRows.map(row => row.id));
      return this.sortedRows.filter(row => selectedIds.has(row.id));
    },
    sortedRows() {
      const { sortBy, sortDesc } = this.options;
      return this._.orderBy(
        this.eventRows,
        sortBy,
        sortDesc.map(desc => (desc ? 'desc' : 'asc')),
      );
    },
  },
  methods: {
    clearSelection() {
      this.selectedRows = [];
    },
    buildEventRow(event) {
      return {
        ...event,
        isSelectable: !event.processing, // used by DataTable
        locomotion: locomotionOf(event),
        photoCount: this.photoCount(event),
        insectCount: this.insectCount(event),
      };
    },
    editEvent(eventRow) {
      this.$emit('editEvent', eventRow);
    },
    hideWhileProcessing(eventRow, value) {
      return eventRow.processing ? '-' : value;
    },
    isTimedOut(item) {
      return isEditingTimedOut(item, Date.now());
    },
    insectCount(event) {
      return this._.sum(Object.values(event.insect_counts));
    },
    photoCount(event) {
      return Object.keys(event.attachments).filter(key => key.startsWith('photo')).length;
    },
    viewEvent(eventRow) {
      const index = this._.findIndex(this.sortedRows, ev => ev.id === eventRow.id);
      this.$emit('viewEvents', { events: this.sortedRows, index });
    },
  },
  watch: {
    sortedSelectedRows(value) {
      this.$emit('sortedSelectedEventsUpdated', value);
    },
  },
};
</script>

<style lang="sass" scoped>
.trap-tag
  gap: 2px
  padding: 2px 6px
  border-radius: 4px
  background: #f5f5f5
  border: 1px solid #d0d0d0
  color: #333
  font-size: 11px
  font-weight: 500
</style>
